import { useCallback } from 'react';
import {
  useLazyGetCartQuery,
  useCreateCartMutation,
  useUpdateCartMutation,
} from '../../../orders/services/orderApi';

/**
 * Add a list of properties to the user's cart.
 *
 * Mirrors the legacy "GET cart → POST or PUT" branch in OrderCartService:
 * - If the user already has a cart, PUT with the union of existing items
 *   and the new selections (deduped by propertyId).
 * - Otherwise, POST a new cart with just the new selections.
 *
 * Returns the resulting line-item count and the number actually added
 * (i.e. selections that weren't already present in the cart). Callers are
 * responsible for dispatching cart-count and surfacing user feedback.
 */
export function useAddPropertiesToCart() {
  const [triggerGetCart] = useLazyGetCartQuery();
  const [createCart] = useCreateCartMutation();
  const [updateCart] = useUpdateCartMutation();

  return useCallback(
    async (
      propertyIds: string[]
    ): Promise<{ itemsCount: number; addedCount: number }> => {
      const userId = localStorage.getItem('userId') ?? '';
      const existingCart = await triggerGetCart(userId, true).unwrap()
        .catch(() => undefined);

      if (existingCart?.cartId) {
        const existingItems = existingCart.items ?? [];
        const existingIds = new Set(existingItems.map((i) => i.propertyId));
        const newSelections = propertyIds
          .filter((id) => !existingIds.has(id))
          .map((id) => ({ propertyId: id }));

        if (newSelections.length === 0) {
          return {
            itemsCount: existingItems.length,
            addedCount: 0,
          };
        }

        const response = await updateCart({
          cartId: existingCart.cartId,
          userId,
          items: [...existingItems, ...newSelections],
        }).unwrap();
        return {
          itemsCount: response.items?.length ?? 0,
          addedCount: newSelections.length,
        };
      }

      const newSelections = propertyIds.map((id) => ({ propertyId: id }));
      const response = await createCart({
        userId,
        items: newSelections,
      }).unwrap();
      return {
        itemsCount: response.items?.length ?? 0,
        addedCount: newSelections.length,
      };
    },
    [triggerGetCart, createCart, updateCart]
  );
}
